import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Global banner shown on /application/* section pages when the signed-in
 * applicant has already submitted their application. The RLS policy on
 * application_progress prevents writes after submission; this banner makes
 * the read-only state clear in the UI.
 */
const LOCKED_PATH_PREFIX = '/application/';
const EXCLUDED_PATHS = new Set<string>([
  '/application/review', // submitted summary view
]);

export const SubmittedLockBanner = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!user?.email) {
        setIsSubmitted(false);
        return;
      }
      const { data } = await supabase
        .from('application_progress')
        .select('is_submitted')
        .eq('email', user.email)
        .maybeSingle();
      if (!cancelled) setIsSubmitted(!!data?.is_submitted);
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [user?.email, pathname]);

  const onLockedRoute =
    pathname.startsWith(LOCKED_PATH_PREFIX) && !EXCLUDED_PATHS.has(pathname);

  if (!isSubmitted || !onLockedRoute) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="sticky top-0 z-50 border-b border-amber-300 bg-amber-50 text-amber-900 shadow-sm"
    >
      <div className="container mx-auto flex items-start gap-3 px-4 py-3">
        <Lock className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <div className="text-sm">
          <p className="font-semibold">Application locked — submitted</p>
          <p className="opacity-90">
            Your application has been submitted and can no longer be edited. If you
            need to make a correction, contact{' '}
            <a
              href="mailto:info-ctsf@ctsf.org"
              className="underline font-medium hover:opacity-80"
            >
              info-ctsf@ctsf.org
            </a>{' '}
            or call 669.244.2531 to request changes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubmittedLockBanner;
